import { makeAutoObservable, action, transaction } from "mobx";

import { getItemLink } from "../utils";
import { RegistryItem } from "../types";

import { EntityType, type Entity } from "@/queries/folder-entities/types";
import { preloadSmartTablePage } from "@/routes/smart-table/loadSmartTablePage";
import { isModifierKeyPressed } from "@/utils/shortcuts";

export class CommandMenuStore {
  private _isOpen = false;
  private _searchQuery = "";
  private _debouncedSearchQuery = "";
  private _debounceTimer?: NodeJS.Timeout;
  private _selectedEntityHandler?: (entity: Entity) => void;
  private _isPendingDebounce = false;
  private _pendingRedirect?: string;

  constructor() {
    makeAutoObservable(this);
  }

  get isOpen() {
    return this._isOpen;
  }

  get searchQuery() {
    return this._searchQuery;
  }

  get debouncedSearchQuery() {
    return this._debouncedSearchQuery;
  }

  get isPendingDebounce() {
    return this._isPendingDebounce;
  }

  @action openSpotlight() {
    this._isOpen = true;
  }

  @action closeSpotlight() {
    this._isOpen = false;
    this._clearSearch();
  }

  @action setSearchQuery(query: string) {
    const previousQuery = this._searchQuery;
    this._searchQuery = query;

    // Set pending debounce if query changed and is non-empty
    if (query !== previousQuery && query.length > 0) {
      this._isPendingDebounce = true;
    } else if (query.length === 0) {
      this._isPendingDebounce = false;
    }

    this._debouncedSetSearchQuery(query);
  }

  @action private _debouncedSetSearchQuery(query: string) {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }

    // If query is empty, update immediately without debounce
    if (query.length === 0) {
      this._debouncedSearchQuery = query;
      this._isPendingDebounce = false;
      return;
    }

    this._debounceTimer = setTimeout(
      action(() => {
        this._debouncedSearchQuery = query;
        this._isPendingDebounce = false;
      }),
      300,
    );
  }

  @action clearSearch() {
    this._searchQuery = "";
    this._debouncedSearchQuery = "";
    this._isPendingDebounce = false;
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
  }

  @action private _clearSearch() {
    this._searchQuery = "";
    this._debouncedSearchQuery = "";
    this._isPendingDebounce = false;
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
  }

  @action setEntityHandler(handler: (entity: Entity) => void) {
    this._selectedEntityHandler = handler;
  }

  @action handleEntitySelect(entity: Entity) {
    transaction(() => {
      this._navigateToEntity(entity);
      this.closeSpotlight();
    });
  }

  @action handleOpenChange(open: boolean) {
    if (!open) {
      this.closeSpotlight();
    }
  }

  @action
  private _navigateToEntity(entity: Entity) {
    const workspaceId = window.location.pathname.split("/")[2];

    if (entity.type === EntityType.SMART_TABLE) {
      preloadSmartTablePage();
    }

    const path = getItemLink(entity as RegistryItem, Number(workspaceId));
    this._pendingRedirect = path;
  }

  get pendingRedirect() {
    return this._pendingRedirect;
  }

  @action
  completeRedirect() {
    this._pendingRedirect = undefined;
  }

  groupEntities(entities: Entity[]): Record<string, Entity[]> {
    if (!entities) {
      return {};
    }

    const grouped = entities.reduce(
      (acc: Record<string, Entity[]>, entity: Entity) => {
        if (!acc[entity.type]) {
          acc[entity.type] = [];
        }
        acc[entity.type].push(entity);
        return acc;
      },
      {} as Record<string, Entity[]>,
    );

    // Sort entities within each group by updated_at (most recent first)
    Object.keys(grouped).forEach(type => {
      grouped[type].sort((a, b) => {
        const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return dateB - dateA;
      });
    });

    return grouped;
  }

  @action setupKeyboardListeners() {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isModifierKeyPressed(e) && e.key === "k") {
        e.preventDefault();
        this.openSpotlight();
      } else if (e.key === "Escape" && this._isOpen) {
        e.preventDefault();
        this.closeSpotlight();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    this._cleanup = () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }

  private _cleanup?: () => void;

  destroy() {
    this._cleanup?.();
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
  }
}
